import { useMemo } from 'react';
import { planConfigs } from './planConfigs';
import { PRECOS } from './stripePrices';
import { useIdioma } from '../i18n';
import { useFormato } from '../i18n/formato';

/**
 * A tabela de planos que as telas exibem.
 *
 * Existiam duas, escritas à mão e divergentes. A home anunciava o que o
 * `planConfigs` de fato aplica; a tela `/plans` anunciava outra coisa:
 *
 *   Basic  — "2 projects, 4000 pages, 14 days history"  →  1 / 3.000 / 7
 *   Guru   — "3 projects, 45 days history"              →  4 / 30
 *   Pro    — "6 projects"                               →  8
 *
 * E oferecia um plano "Enterprise" que não existe em lugar nenhum: o quarto
 * plano se chama Business no `planConfigs`, no banco e no mapa do Stripe.
 * Quem clicasse ali entrava num fluxo sem preço e sem plano correspondente.
 *
 * Nenhuma dessas telas estava errada por descuido — estavam erradas porque os
 * números eram digitados nelas. Aqui os limites saem de `planConfigs`, que é o
 * mesmo objeto que o backend usa para barrar a criação de projeto e apagar
 * dados vencidos, e o preço sai de `stripePrices`, conferido contra o Stripe.
 * O que sobra escrito à mão é texto: descrição e destaque, que não têm como
 * mentir sobre um limite.
 */

export const ORDEM = ['Basic', 'Guru', 'Pro', 'Business'];

const SUPORTE = {
  email: 'plano.suporteEmail',
  chat: 'plano.suporteChat',
  priority_chat: 'plano.suportePrioritario',
  dedicated: 'plano.suporteDedicado'
};

/* O conector MCP fica de Guru para cima: é a lista `PLANOS_DO_CONECTOR` de
   `routes/mcp.js` no backend, que recusa o Basic no login do conector. */
const TEXTO = {
  Basic: { descricao: 'plano.basicDescricao', destaques: ['plano.basicDestaque'] },
  Guru: {
    descricao: 'plano.guruDescricao',
    destaques: ['plano.guruDestaque', 'plano.conectorMcp'],
    popular: true
  },
  Pro: { descricao: 'plano.proDescricao', destaques: ['plano.proDestaque', 'plano.conectorMcp'] },
  Business: {
    descricao: 'plano.businessDescricao',
    destaques: ['plano.businessDestaque'],
    contato: true
  }
};

/**
 * A tabela deixou de ser uma constante porque passou a depender do idioma.
 *
 * `PLANOS` era montado uma vez, no carregamento do módulo, com texto em inglês
 * embutido. Isso funcionava enquanto havia um idioma só; com três, a lista
 * ficaria congelada no idioma que estivesse ativo quando o módulo carregou — e
 * como carrega antes de a sessão responder, seria sempre o de entrada.
 *
 * Os limites continuam vindo de `planConfigs` e o preço de `stripePrices`: o
 * que o dicionário fornece é só texto.
 */
const montarRecursos = (nome, t, numero) => {
  const { limits, features } = planConfigs[nome];
  return [
    typeof limits.projects === 'number'
      ? t('plano.projetos', { n: limits.projects })
      : t('plano.projetosIlimitados'),
    typeof limits.pages === 'number'
      ? t('plano.paginasPorMes', { n: numero(limits.pages) })
      : t('plano.paginasIlimitadas'),
    typeof features.monthly_credits === 'number'
      ? t('plano.creditosPorMes', { n: numero(features.monthly_credits) })
      : t('plano.creditosIlimitados'),
    ...TEXTO[nome].destaques.map((chave) => t(chave)),
    t(SUPORTE[features.support_type] || SUPORTE.email),
    typeof features.data_retention_days === 'number'
      ? t('plano.retencao', { n: features.data_retention_days })
      : t('plano.retencaoIlimitada')
  ];
};

export const usePlanos = () => {
  const { t } = useIdioma();
  const { numero } = useFormato();

  return useMemo(
    () =>
      ORDEM.map((nome) => ({
        nome,
        descricao: t(TEXTO[nome].descricao),
        popular: TEXTO[nome].popular || false,
        contato: TEXTO[nome].contato || false,
        // Business é sob consulta: não há preço de tabela nem price ID no Stripe.
        preco: PRECOS[nome] || null,
        recursos: montarRecursos(nome, t, numero)
      })),
    [t, numero]
  );
};

/* `resumoDoPlano` saiu daqui em vez de virar hook: nasceu junto com as telas
   autenticadas (d36c4f6) e nenhuma tela chegou a chamá-lo. Traduzi-lo seria
   traduzir código morto. */

/** Posição na escada, para decidir entre "Upgrade" e "Downgrade". */
export const nivelDoPlano = (nome) => ORDEM.indexOf(nome);
